import type {
  ExecutionContext,
  NewSubcontractorDocument,
  ProjectSubcontract,
  SubcontractDetails,
  SubcontractingRepository,
  Subcontractor,
  SubcontractorDetails,
  SubcontractorDocument,
  SubcontractorDocumentDates,
  SubcontractInvoice,
  SubcontractInvoiceDetails,
  SubcontractPayment,
  SubcontractPaymentDetails,
  SubcontractPlanningLink,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown) => Number(v ?? 0);

function toSubcontractor(row: Row): Subcontractor {
  return {
    id: row.id,
    name: row.name,
    trade: row.trade ?? null,
    siret: row.siret ?? null,
    contactName: row.contact_name ?? null,
    phone: row.phone ?? null,
    email: row.email ?? null,
    address: row.address ?? null,
    legalForm: row.legal_form ?? null,
    registration: row.registration ?? null,
    signatoryName: row.signatory_name ?? null,
    signatoryRole: row.signatory_role ?? null,
    isSme: row.is_sme ?? null,
    bankName: row.bank_name ?? null,
    iban: row.iban ?? null,
    notes: row.notes ?? null,
    archived: row.archived_at != null,
  };
}

/** Lu aussi par la fiche chantier, avec la marge. */
export const SUBCONTRACT_COLUMNS =
  "id, project_id, subcontractor_id, lot_id, phase_id, scope, amount_ht, status, signed_on, notes, " +
  "approval_status, presented_on, approval_decided_on, payment_guarantee, " +
  "public_contract_ref, price_revision, advance_requested, " +
  "subcontractor:subcontractors(name)";

export function toSubcontract(row: Row): ProjectSubcontract {
  const st = Array.isArray(row.subcontractor) ? row.subcontractor[0] : row.subcontractor;
  return {
    id: row.id,
    projectId: row.project_id,
    subcontractorId: row.subcontractor_id,
    subcontractorName: st?.name ?? "",
    lotId: row.lot_id ?? null,
    phaseId: row.phase_id ?? null,
    scope: row.scope ?? null,
    amountHt: num(row.amount_ht),
    status: row.status,
    signedOn: row.signed_on ?? null,
    approvalStatus: row.approval_status ?? "not_presented",
    presentedOn: row.presented_on ?? null,
    approvalDecidedOn: row.approval_decided_on ?? null,
    paymentGuarantee: row.payment_guarantee ?? "none",
    publicContractRef: row.public_contract_ref ?? null,
    priceRevision: row.price_revision ?? null,
    advanceRequested: row.advance_requested ?? false,
    notes: row.notes ?? null,
  };
}

/** Lu aussi par la fiche chantier. */
export const INVOICE_COLUMNS =
  "id, project_id, subcontract_id, invoice_number, invoice_date, due_date, amount_ht, vat_mode, vat_rate, " +
  "retention_percent, notes";

export function toSubcontractInvoice(row: Row): SubcontractInvoice {
  return {
    id: row.id,
    projectId: row.project_id,
    subcontractId: row.subcontract_id,
    invoiceNumber: row.invoice_number,
    invoiceDate: row.invoice_date,
    dueDate: row.due_date ?? null,
    amountHt: num(row.amount_ht),
    vatMode: row.vat_mode,
    vatRate: num(row.vat_rate),
    retentionPercent: num(row.retention_percent),
    notes: row.notes ?? null,
  };
}

export const PAYMENT_COLUMNS = "id, project_id, subcontract_id, kind, invoice_id, amount, paid_on, paid_by, notes";

export function toSubcontractPayment(row: Row): SubcontractPayment {
  return {
    id: row.id,
    projectId: row.project_id,
    subcontractId: row.subcontract_id,
    kind: row.kind,
    invoiceId: row.invoice_id ?? null,
    amount: num(row.amount),
    paidOn: row.paid_on,
    paidBy: row.paid_by ?? "company",
    notes: row.notes ?? null,
  };
}

const PLANNING_LINK_COLUMNS =
  "id, project_id, subcontract_id, expires_at, revoked_at, last_seen_at, view_count, created_at";

function toPlanningLink(row: Row): SubcontractPlanningLink {
  return {
    id: row.id,
    projectId: row.project_id,
    subcontractId: row.subcontract_id,
    expiresAt: row.expires_at,
    revokedAt: row.revoked_at ?? null,
    lastSeenAt: row.last_seen_at ?? null,
    viewCount: Number(row.view_count ?? 0),
    createdAt: row.created_at,
  };
}

/** Les erreurs propres aux factures et règlements. */
function failInvoicing(error: { message: string; code?: string }): never {
  if (error.code === "23505") {
    throw new DomainError("conflict", "Une facture de ce numéro est déjà enregistrée sur ce marché.", {
      pgCode: error.code,
    });
  }
  if (error.code === "23503") {
    throw new DomainError("conflict", "Ce marché ou cette facture n'existe plus.", { pgCode: error.code });
  }
  return fail(error);
}

/** Le bucket des pièces, déjà fermé au terrain. */
const BUCKET = "documents";

const DOCUMENT_COLUMNS =
  "id, subcontractor_id, kind, label, issued_on, expires_on, storage_path, file_name, notes, created_at";

function toDocument(row: Row): SubcontractorDocument {
  return {
    id: row.id,
    subcontractorId: row.subcontractor_id,
    kind: row.kind,
    label: row.label ?? null,
    issuedOn: row.issued_on ?? null,
    expiresOn: row.expires_on ?? null,
    storagePath: row.storage_path ?? null,
    fileName: row.file_name ?? null,
    notes: row.notes ?? null,
    createdAt: row.created_at,
  };
}

function fail(error: { message: string; code?: string }): never {
  // 23505 : le seul index unique du carnet est le SIRET.
  if (error.code === "23505") {
    throw new DomainError("conflict", "Un sous-traitant de ce SIRET est déjà au carnet.", {
      pgCode: error.code,
    });
  }
  // 23503 : le lot ou la phase n'est pas sur ce chantier, ou le
  // sous-traitant n'est pas de ce compte.
  if (error.code === "23503") {
    throw new DomainError("conflict", "Ce lot, cette phase ou ce sous-traitant n'est pas sur ce chantier.", {
      pgCode: error.code,
    });
  }
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseSubcontractingRepository(
  clientFor: DbClientFactory,
): SubcontractingRepository {
  /** Le chantier d'un marché : une facture et un règlement le recopient. */
  async function chantierDuMarche(ctx: ExecutionContext, subcontractId: string): Promise<string> {
    const { data, error } = await clientFor(ctx)
      .from("project_subcontracts")
      .select("project_id")
      .eq("id", subcontractId)
      .maybeSingle();
    if (error) fail(error);
    if (!data) throw new DomainError("not_found", "Ce marché n'existe pas.", { id: subcontractId });
    return (data as Row).project_id as string;
  }

  return {
    async listSubcontractors(ctx: ExecutionContext, includeArchived: boolean) {
      let query = clientFor(ctx).from("subcontractors").select("*").order("name");
      if (!includeArchived) query = query.is("archived_at", null);
      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toSubcontractor);
    },

    async saveSubcontractor(ctx: ExecutionContext, id: string | undefined, d: SubcontractorDetails) {
      const db = clientFor(ctx);
      const columns = {
        name: d.name,
        trade: d.trade ?? null,
        siret: d.siret ?? null,
        contact_name: d.contactName ?? null,
        phone: d.phone ?? null,
        email: d.email ?? null,
        address: d.address ?? null,
        legal_form: d.legalForm ?? null,
        registration: d.registration ?? null,
        signatory_name: d.signatoryName ?? null,
        signatory_role: d.signatoryRole ?? null,
        is_sme: d.isSme ?? null,
        bank_name: d.bankName ?? null,
        iban: d.iban ?? null,
        notes: d.notes ?? null,
      };
      const { data, error } = id
        ? await db.from("subcontractors").update(columns).eq("id", id).select().maybeSingle()
        : await db.from("subcontractors").insert(columns).select().single();
      if (error) fail(error);
      if (!data) throw new DomainError("not_found", "Ce sous-traitant n'existe pas.", { id });
      return toSubcontractor(data);
    },

    async setSubcontractorArchived(ctx: ExecutionContext, id: string, archived: boolean) {
      const { data, error } = await clientFor(ctx)
        .from("subcontractors")
        .update({ archived_at: archived ? new Date().toISOString() : null })
        .eq("id", id)
        .select("id");
      if (error) fail(error);
      if (!data?.length) throw new DomainError("not_found", "Ce sous-traitant n'existe pas.", { id });
    },

    async saveSubcontract(ctx: ExecutionContext, id: string | undefined, d: SubcontractDetails) {
      const db = clientFor(ctx);
      const columns = {
        project_id: d.projectId,
        subcontractor_id: d.subcontractorId,
        lot_id: d.lotId ?? null,
        phase_id: d.phaseId ?? null,
        scope: d.scope ?? null,
        amount_ht: d.amountHt,
        status: d.status,
        signed_on: d.signedOn ?? null,
        approval_status: d.approvalStatus,
        presented_on: d.presentedOn ?? null,
        approval_decided_on: d.approvalDecidedOn ?? null,
        payment_guarantee: d.paymentGuarantee,
        public_contract_ref: d.publicContractRef ?? null,
        price_revision: d.priceRevision ?? null,
        advance_requested: d.advanceRequested ?? false,
        notes: d.notes ?? null,
      };
      const { data, error } = id
        ? await db.from("project_subcontracts").update(columns).eq("id", id).select(SUBCONTRACT_COLUMNS).maybeSingle()
        : await db.from("project_subcontracts").insert(columns).select(SUBCONTRACT_COLUMNS).single();
      if (error) fail(error);
      if (!data) throw new DomainError("not_found", "Ce marché n'existe pas.", { id });
      return toSubcontract(data);
    },

    async listContractStatuses(ctx: ExecutionContext) {
      const { data, error } = await clientFor(ctx).from("project_subcontracts")
        .select("subcontractor_id, status, amount_ht");
      if (error) fail(error);
      return (data ?? []).map((r: Row) => ({
        subcontractorId: r.subcontractor_id,
        status: r.status,
        amountHt: Number(r.amount_ht),
      }));
    },

    async listDocuments(ctx: ExecutionContext) {
      const { data, error } = await clientFor(ctx)
        .from("subcontractor_documents")
        .select(DOCUMENT_COLUMNS)
        .order("created_at", { ascending: false });
      if (error) fail(error);
      return (data ?? []).map(toDocument);
    },

    async addDocument(ctx: ExecutionContext, d: NewSubcontractorDocument) {
      const { data, error } = await clientFor(ctx)
        .from("subcontractor_documents")
        .insert({
          subcontractor_id: d.subcontractorId,
          kind: d.kind,
          label: d.label,
          issued_on: d.issuedOn,
          expires_on: d.expiresOn,
          storage_path: d.storagePath,
          file_name: d.fileName,
          mime_type: d.mimeType,
          size_bytes: d.sizeBytes,
          notes: d.notes,
        })
        .select(DOCUMENT_COLUMNS)
        .single();
      if (error) fail(error);
      return toDocument(data);
    },

    async updateDocument(ctx: ExecutionContext, id: string, d: SubcontractorDocumentDates) {
      const { data, error } = await clientFor(ctx)
        .from("subcontractor_documents")
        .update({ label: d.label, issued_on: d.issuedOn, expires_on: d.expiresOn, notes: d.notes })
        .eq("id", id)
        .select("id");
      if (error) fail(error);
      if (!data?.length) throw new DomainError("not_found", "Cette pièce n'existe pas.", { id });
    },

    async removeDocument(ctx: ExecutionContext, id: string) {
      const db = clientFor(ctx);
      const { data, error } = await db
        .from("subcontractor_documents")
        .delete()
        .eq("id", id)
        .select("storage_path");
      if (error) fail(error);
      if (!data?.length) throw new DomainError("not_found", "Cette pièce n'existe pas.", { id });
      // La ligne d'abord, le fichier ensuite : un fichier resté orphelin ne
      // se voit nulle part, une ligne sans fichier promettrait une pièce
      // qu'on ne peut plus ouvrir.
      const path = (data[0] as Row).storage_path as string | null;
      if (path) await db.storage.from(BUCKET).remove([path]);
    },

    async listInvoicing(ctx: ExecutionContext, filter: { projectId: string } | { subcontractId: string }) {
      const db = clientFor(ctx);
      const [col, val] = "projectId" in filter ? ["project_id", filter.projectId] : ["subcontract_id", filter.subcontractId];
      const [invoices, payments] = await Promise.all([
        db.from("subcontract_invoices").select(INVOICE_COLUMNS).eq(col, val).order("invoice_date"),
        db.from("subcontract_payments").select(PAYMENT_COLUMNS).eq(col, val).order("paid_on"),
      ]);
      if (invoices.error) fail(invoices.error);
      if (payments.error) fail(payments.error);
      return {
        invoices: (invoices.data ?? []).map(toSubcontractInvoice),
        payments: (payments.data ?? []).map(toSubcontractPayment),
      };
    },

    async saveInvoice(ctx: ExecutionContext, id: string | undefined, d: SubcontractInvoiceDetails) {
      const db = clientFor(ctx);
      const columns = {
        invoice_number: d.invoiceNumber,
        invoice_date: d.invoiceDate,
        due_date: d.dueDate ?? null,
        amount_ht: d.amountHt,
        vat_mode: d.vatMode,
        vat_rate: d.vatRate,
        retention_percent: d.retentionPercent,
        notes: d.notes ?? null,
      };
      if (id) {
        const { data, error } = await db
          .from("subcontract_invoices")
          .update(columns)
          .eq("id", id)
          .select(INVOICE_COLUMNS)
          .maybeSingle();
        if (error) failInvoicing(error);
        if (!data) throw new DomainError("not_found", "Cette facture n'existe pas.", { id });
        return toSubcontractInvoice(data);
      }
      const projectId = await chantierDuMarche(ctx, d.subcontractId);
      const { data, error } = await db
        .from("subcontract_invoices")
        .insert({ ...columns, subcontract_id: d.subcontractId, project_id: projectId })
        .select(INVOICE_COLUMNS)
        .single();
      if (error) failInvoicing(error);
      return toSubcontractInvoice(data);
    },

    async removeInvoice(ctx: ExecutionContext, id: string) {
      const { data, error } = await clientFor(ctx).from("subcontract_invoices").delete().eq("id", id).select("id");
      if (error) failInvoicing(error);
      if (!data?.length) {
        throw new DomainError("not_found", "Cette facture n'existe pas, ou votre rôle ne permet pas de la retirer.", {
          id,
        });
      }
    },

    async savePayment(ctx: ExecutionContext, id: string | undefined, d: SubcontractPaymentDetails) {
      const db = clientFor(ctx);
      const columns = {
        kind: d.kind,
        invoice_id: d.invoiceId ?? null,
        amount: d.amount,
        paid_on: d.paidOn,
        paid_by: d.paidBy,
        notes: d.notes ?? null,
      };
      if (id) {
        const { data, error } = await db
          .from("subcontract_payments")
          .update(columns)
          .eq("id", id)
          .select(PAYMENT_COLUMNS)
          .maybeSingle();
        if (error) failInvoicing(error);
        if (!data) throw new DomainError("not_found", "Ce règlement n'existe pas.", { id });
        return toSubcontractPayment(data);
      }
      const projectId = await chantierDuMarche(ctx, d.subcontractId);
      const { data, error } = await db
        .from("subcontract_payments")
        .insert({ ...columns, subcontract_id: d.subcontractId, project_id: projectId })
        .select(PAYMENT_COLUMNS)
        .single();
      if (error) failInvoicing(error);
      return toSubcontractPayment(data);
    },

    async removePayment(ctx: ExecutionContext, id: string) {
      const { data, error } = await clientFor(ctx).from("subcontract_payments").delete().eq("id", id).select("id");
      if (error) failInvoicing(error);
      if (!data?.length) {
        throw new DomainError("not_found", "Ce règlement n'existe pas, ou votre rôle ne permet pas de le retirer.", {
          id,
        });
      }
    },

    async findSubcontract(ctx: ExecutionContext, id: string) {
      const { data, error } = await clientFor(ctx)
        .from("project_subcontracts")
        .select(SUBCONTRACT_COLUMNS)
        .eq("id", id)
        .maybeSingle();
      if (error) fail(error);
      return data ? toSubcontract(data) : null;
    },

    async listOpenContracts(ctx: ExecutionContext) {
      const { data, error } = await clientFor(ctx)
        .from("project_subcontracts")
        .select(`${SUBCONTRACT_COLUMNS}, project:projects(name)`)
        .in("status", ["draft", "signed"]);
      if (error) fail(error);
      return (data ?? []).map((r: Row) => {
        const p = Array.isArray(r.project) ? r.project[0] : r.project;
        return { ...toSubcontract(r), projectName: p?.name ?? "" };
      });
    },

    async openPlanningLink(ctx: ExecutionContext, subcontractId: string) {
      const { data, error } = await clientFor(ctx).rpc("open_subcontract_planning", {
        p_subcontract_id: subcontractId,
      });
      if (error) fail(error);
      return data as string;
    },

    async listPlanningLinks(ctx: ExecutionContext, projectId: string) {
      const { data, error } = await clientFor(ctx)
        .from("subcontract_planning_links")
        .select(PLANNING_LINK_COLUMNS)
        .eq("project_id", projectId)
        .order("created_at", { ascending: false });
      if (error) fail(error);
      return (data ?? []).map(toPlanningLink);
    },

    async revokePlanningLink(ctx: ExecutionContext, id: string) {
      // Marqué, jamais supprimé : la trace d'un lien qui a servi reste.
      const { data, error } = await clientFor(ctx)
        .from("subcontract_planning_links")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", id)
        .is("revoked_at", null)
        .select("id");
      if (error) fail(error);
      if (!data?.length) throw new DomainError("not_found", "Ce lien n'existe pas, ou il est déjà fermé.", { id });
    },

    async removeSubcontract(ctx: ExecutionContext, id: string) {
      const { data, error } = await clientFor(ctx).from("project_subcontracts").delete().eq("id", id).select("id");
      // 23503 : des factures le désignent. Elles restent : le marché s'annule.
      if (error?.code === "23503") {
        throw new DomainError(
          "conflict",
          "Ce marché a des factures : il ne se retire pas, il s'annule. Ses factures restent lisibles.",
          { pgCode: error.code },
        );
      }
      if (error) fail(error);
      if (!data?.length) {
        throw new DomainError("not_found", "Ce marché n'existe pas, ou votre rôle ne permet pas de le retirer.", { id });
      }
    },
  };
}
